import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getLandingPageTitleColor } from "../../functions/themeAssets";
import GameStageLayout from "./GameStageLayout";
import "./gameStageLayout.css";
import "../fonts/breuer-headline.css";
import "./imageQuizStage.css";
import "./quizStage.css";
import {
  getImageQuizChoiceSlots,
  getImageQuizPointsForChoice,
  parseImageQuizFromTheme,
} from "./imageQuizStageUtils";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveGameStageAndReport,
  setStageActive,
  updateExternalReport,
} from "../../functions/stageReportSync";
import { showStageCompleteCelebration, navigateAfterCelebration } from "../../functions/stageCelebration";
import {
  fireStageHelpSwal,
  getStageRulesFromTheme,
} from "../../functions/stageRules";

const STAGE_NUM = 4;

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Stage4() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage4Points, setStage4Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [selections, setSelections] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = storedUser.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  const questions = useMemo(() => parseImageQuizFromTheme(themeData), [themeData]);

  const title = useMemo(() => {
    const t =
      themeData?.image_quiz_title ||
      themeData?.landing_page_title ||
      themeData?.main_title ||
      themeData?.themename ||
      "Image Quiz";
    return String(t).trim();
  }, [themeData]);

  const subtitle = useMemo(() => {
    const custom = themeData?.image_quiz_subtitle;
    if (custom && String(custom).trim()) return String(custom).trim();
    return "We have Bollywood's biggest hub! Let's take this fun Emoji challenge to identify the songs.";
  }, [themeData]);

  const titleTextColor = useMemo(() => getLandingPageTitleColor(themeData), [themeData]);
  const themedTextStyle = useMemo(
    () => (titleTextColor ? { color: titleTextColor } : undefined),
    [titleTextColor]
  );

  const pointsPerQuestion = useMemo(() => {
    const themeDefault = Number(themeData?.points);
    return Number.isFinite(themeDefault) && themeDefault > 0
      ? Math.trunc(themeDefault)
      : 1;
  }, [themeData?.points]);

  const answeredCount = useMemo(
    () => Object.keys(selections).filter((k) => selections[k]).length,
    [selections]
  );

  const allAnswered = questions.length > 0 && answeredCount === questions.length;

  /** Desktop columns: 3 → one row; 4 → 2×2; 5 → 3+2; 6+ → 3 per row. */
  const preferredCols = useMemo(() => {
    const n = questions.length;
    if (n <= 0) return 1;
    if (n <= 3) return n;
    if (n === 4) return 2;
    return 3;
  }, [questions.length]);

  const [gridCols, setGridCols] = useState(preferredCols);

  useEffect(() => {
    const mq960 = window.matchMedia("(max-width: 960px)");
    const mq640 = window.matchMedia("(max-width: 640px)");
    const mqPortrait = window.matchMedia(
      "(max-width: 640px) and (orientation: portrait)"
    );

    const updateGridCols = () => {
      if (mq640.matches && mqPortrait.matches) {
        setGridCols(Math.min(2, preferredCols));
      } else if (mq640.matches) {
        setGridCols(1);
      } else if (mq960.matches) {
        setGridCols(Math.min(2, preferredCols));
      } else {
        setGridCols(preferredCols);
      }
    };

    updateGridCols();
    mq960.addEventListener("change", updateGridCols);
    mq640.addEventListener("change", updateGridCols);
    mqPortrait.addEventListener("change", updateGridCols);
    return () => {
      mq960.removeEventListener("change", updateGridCols);
      mq640.removeEventListener("change", updateGridCols);
      mqPortrait.removeEventListener("change", updateGridCols);
    };
  }, [preferredCols]);

  const questionRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < questions.length; i += gridCols) {
      rows.push(questions.slice(i, i + gridCols));
    }
    return rows;
  }, [questions, gridCols]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("image-quiz-page");
    return () => {
      document.body.classList.remove("image-quiz-page");
    };
  }, []);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();

    const tick = setInterval(() => {
      const sessionSec = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage4Points);
  }, [stage4Points]);

  const finishStage = useCallback(
    async (skip = false) => {
      if (submitting || submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);

      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;
      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stageId = sessionStorage.getItem(STAGE_ROW_KEY);

      let stage4Score = 0;
      if (!skip) {
        stage4Score = questions.reduce((sum, q, idx) => {
          const choiceKey = selections[idx];
          if (!choiceKey) return sum;
          return sum + getImageQuizPointsForChoice(q, choiceKey, pointsPerQuestion);
        }, 0);
        setStage4Points(stage4Score);
      }

      const finalTotal = otherStagesTotalRef.current + stage4Score;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        try {
          await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId,
            stageNumber: STAGE_NUM,
            score: stage4Score,
            time: timeStr,
            total_score: finalTotal,
            currentQues: skip ? answeredCount : questions.length,
          });
          await updateExternalReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            points: finalTotal,
            time: timeStr,
            stageId,
          });
        } catch (err) {
          console.error("Stage4 finish:", err);
        }
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);

      await showStageCompleteCelebration({
        stageNumber: STAGE_NUM,
        skip,
        score: stage4Score,
      });

      if (isDemoBypass) {
        window.location.href = String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      navigateAfterCelebration(navigate, "/stage5");
    },
    [
      submitting,
      storedUser,
      isDemoBypass,
      backendBase,
      adminToken,
      questions,
      selections,
      pointsPerQuestion,
      answeredCount,
      navigate,
    ]
  );

  const handleSkip = useCallback(() => {
    finishStage(true);
  }, [finishStage]);

  const showHelp = () => {
    fireStageHelpSwal({
      rules: getStageRulesFromTheme(themeData, "image_quiz_rules"),
      themeData,
    });
  };

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      if (themeStatus !== "succeeded") return;

      if (!questions.length) {
        if (!cancelled) setDbReady(true);
        return;
      }

      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        const rowId = sessionStorage.getItem(STAGE_ROW_KEY);
        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));
        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st, {
            stageNumber: STAGE_NUM,
            questionCount: questions.length,
          });
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM, {
            stageNumber: STAGE_NUM,
            questionCount: questions.length,
          });
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage4 = Number(st.stage4) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage4);
          setStage4Points(dbStage4);
          setDisplayTotalScore(dbTotal);
        }

        setStageActive(STAGE_NUM, storedUser);
        if (!cancelled) setDbReady(true);
      } catch (err) {
        console.error("Stage4 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [
    themeStatus,
    questions.length,
    backendBase,
    adminToken,
    isDemoBypass,
    storedUser,
    navigate,
  ]);

  const handleSelect = (questionIndex, choiceKey) => {
    if (submitting) return;
    setSelections((prev) => ({ ...prev, [questionIndex]: choiceKey }));
  };

  const handleSubmit = () => {
    if (!allAnswered || submitting) return;
    finishStage(false);
  };

  if (themeStatus === "loading" || themeStatus === "idle" || !dbReady) {
    return <div className="image-quiz-stage__loading">Loading game…</div>;
  }

  if (!questions.length) {
    return (
      <div className="image-quiz-stage__empty">
        No image quiz questions configured. Add them in theme admin under Image Quiz.
      </div>
    );
  }

  return (
    <GameStageLayout
      title={title}
      subtitle={subtitle}
      themedTextStyle={themedTextStyle}
      elapsedClock={formatSecondsToClock(elapsedSec)}
      points={displayTotalScore}
      onHelp={showHelp}
      onSkip={handleSkip}
      submitting={submitting}
      accent="purple"
      className="game-stage--image-quiz"
      footer={
        <button
          type="button"
          className="game-stage__submit-btn"
          disabled={!allAnswered || submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Submitting…" : "SUBMIT"}
        </button>
      }
    >
      <div className="image-quiz-stage__board">
        <div
          className={`image-quiz-stage__list image-quiz-stage__list--cols-${gridCols}`}
        >
          {questionRows.map((row, rowIdx) => {
            const isPartialRow = row.length > 0 && row.length < gridCols;
            const isPartialPair = isPartialRow && row.length === 2;
            return (
            <div
              key={`row-${rowIdx}`}
              className={`image-quiz-stage__row${
                isPartialRow ? " image-quiz-stage__row--partial" : ""
              }${isPartialPair ? " image-quiz-stage__row--partial-2" : ""}`}
            >
              {row.map((question, colIdx) => {
                const idx = rowIdx * gridCols + colIdx;
                const slots = getImageQuizChoiceSlots(question);

                return (
                  <article
                    key={`${question.image || idx}-${idx}`}
                    className="image-quiz-card"
                  >
                    <div className="image-quiz-card__pill">
                      {question.image ? (
                        <img
                          className="image-quiz-card__image"
                          src={question.image}
                          alt={`Question ${idx + 1}`}
                        />
                      ) : null}
                    </div>
                    <div
                      className="image-quiz-card__options"
                      role="radiogroup"
                      aria-label={`Question ${idx + 1}`}
                    >
                      {slots.map((choice) => {
                        const checked = selections[idx] === choice.key;
                        return (
                          <label
                            key={choice.key}
                            className={`image-quiz-card__option${
                              checked ? " image-quiz-card__option--selected" : ""
                            }`}
                          >
                            <input
                              type="radio"
                              name={`image-quiz-q-${idx}`}
                              value={choice.key}
                              checked={checked}
                              disabled={submitting}
                              onChange={() => handleSelect(idx, choice.key)}
                            />
                            <span className="image-quiz-card__radio" aria-hidden />
                            <span className="image-quiz-card__label" style={themedTextStyle}>
                              {choice.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </article>
                );
              })}
            </div>
            );
          })}
        </div>
      </div>
    </GameStageLayout>
  );
}
